import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

export interface SidebarContextType {
  mini: boolean;
  fullyExpanded: boolean;
  fullyCollapsed: boolean;
  hasDrawerTransitions: boolean;
  expandedItemIds: string[];
  onPageItemClick?: (id: string, hasNestedNavigation: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const theme = useTheme();
  const isOverSmViewport = useMediaQuery(theme.breakpoints.up("sm"));

  const [mini] = useState(false);
  const [fullyExpanded, setFullyExpanded] = useState(true);
  const [fullyCollapsed] = useState(false);
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);
  const [hasDrawerTransitions] = useState(true);

  const onPageItemClick = useCallback(
    (itemId: string, hasNestedNavigation: boolean) => {
      if (hasNestedNavigation && !mini) {
        setExpandedItemIds((previousValue) =>
          previousValue.includes(itemId)
            ? previousValue.filter((id) => id !== itemId)
            : [...previousValue, itemId],
        );
      } else if (!isOverSmViewport && !hasNestedNavigation) {
        setFullyExpanded(false);
      }
    },
    [mini, isOverSmViewport],
  );

  const value = useMemo(
    () => ({
      onPageItemClick,
      mini,
      fullyExpanded,
      fullyCollapsed,
      hasDrawerTransitions,
      expandedItemIds,
    }),
    [
      onPageItemClick,
      mini,
      fullyExpanded,
      fullyCollapsed,
      hasDrawerTransitions,

      expandedItemIds,
    ],
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export default SidebarContext;
